import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generatePath } from 'react-router-dom';
import { Response, Pagination } from '@ui/src';
import {
  RiskScoreItem,
  RiskScoreCalculationType,
  ProductRiskListItem,
  ProductsValidators,
} from '../../../types';
import { corporateClientsRiskApi } from '../services';
import ROUTES from '../../../../routes';

export interface CorporateClientsProductsListState {
  isLoading: boolean;
  requestInProgress: boolean;
  values?: ProductRiskListItem[];
  sort?: {
    sortProp: string;
    sortDir: string;
  };
  calculatedBy?: RiskScoreCalculationType;
  submitInProgress: boolean;
  pagination?: Pagination;
  validators?: ProductsValidators;
  selectedList: string[];
  errorsList?: string;
}

const initialState: CorporateClientsProductsListState = {
  isLoading: true,
  requestInProgress: false,
  values: undefined,
  submitInProgress: false,
  selectedList: [],
};

export const corporateClientsProductsListSlice = createSlice({
  name: 'corporateClientsProductsList',
  initialState,
  reducers: {
    selectList: (state, action: PayloadAction<string[]>) => {
      state.selectedList = action.payload;
    },
    clearProductRiskListState: (state) => {
      state.isLoading = true;
      state.requestInProgress = false;
      state.values = undefined;
      state.errorsList = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      corporateClientsRiskApi.endpoints.getProductsValidatorsCorporate
        .matchFulfilled,
      (state, action: PayloadAction<{ data: ProductsValidators }>) => {
        state.validators = action.payload.data;
      },
    );
    builder.addMatcher(
      corporateClientsRiskApi.endpoints.getProductsCorporate.matchPending,
      (state) => {
        state.errorsList = undefined;
      },
    );
    builder.addMatcher(
      corporateClientsRiskApi.endpoints.getProductsCorporate.matchRejected,
      (state, action: any) => {
        state.values = undefined;
        state.pagination = undefined;

        if (action?.payload.data) {
          if (Array.isArray(action?.payload.data?.message)) {
            state.errorsList = action?.payload.data?.message
              ?.map((item: { value: string }) => item.value)
              .join('; ');
          } else {
            state.errorsList = action?.payload.data?.error;
          }
        }
      },
    );
    builder.addMatcher(
      corporateClientsRiskApi.endpoints.getProductsCorporate.matchFulfilled,
      (state, action: PayloadAction<Response<RiskScoreItem>>) => {
        state.values = action.payload.data.edges
          .map((item) => ({
            id: item.node.id,
            riskScore: item.node.riskScore,
            name: item.node.name,
            scoreAction: item.node.riskScoreRange.action,
            riskCategory: item.node.riskScoreRange.name,
            riskScoreRangeId: item.node.riskScoreRangeId,
            updatedAt: item.node.updatedAt,
            url: generatePath(ROUTES.CORPORATE_CLIENTS_RISK_PRODUCTS_EDIT, {
              productId: item.node.id,
            }),
          }))
          .sort((a, b) => b.riskScore - a.riskScore);

        state.pagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      corporateClientsRiskApi.endpoints.updateCorporateProduct.matchFulfilled,
      (state, action: PayloadAction<{ data: RiskScoreItem }>) => {
        const { data } = action.payload;

        const nextList = state.values?.map((item) => {
          let result = { ...item };

          if (result.id === data.id) {
            result = {
              id: data.id,
              riskScore: data.riskScore,
              name: data.name,
              scoreAction: data.riskScoreRange.action,
              riskCategory: data.riskScoreRange.name,
              riskScoreRangeId: data.riskScoreRangeId,
              updatedAt: data.updatedAt,
              url: generatePath(ROUTES.CORPORATE_CLIENTS_RISK_PRODUCTS_EDIT, {
                productId: data.id,
              }),
            };
          }

          return result;
        });

        state.values = nextList;
      },
    );
  },
});

export const { clearProductRiskListState, selectList } =
  corporateClientsProductsListSlice.actions;

export default corporateClientsProductsListSlice.reducer;
